import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Button, Drawer, InlineError, Input, Select, Textarea } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useTapPeople } from '../projects/hooks';
import { Timeline } from '../profiles/Timeline';
import { ProspectHistorySection } from '../profiles/ProspectHistorySection';
import { MessageSelectionDialog } from './MessageSelectionDialog';
import { JourneyOperationDialog } from './JourneyOperationDialog';
import { useJourneyAdmin } from './useJourneyAdmin';
import { useProspectingAvailability } from './useProspectingAvailability';
import { useDeleteProspect } from './prospects';
import { NextActionBlock } from './NextActionBlock';
import {
  canQueue,
  latestByProspect,
  requestStatusLabel,
  useQueueProspectResearch,
  useResearchQueue,
} from './researchQueue';
import { leadHref } from '../profiles/profileLinks';
import { leadActivityItems, mergeTimeline } from '../profiles/timelineItems';
import { activityDayLabel, actorLabel } from './leadActivity';
import {
  PROSPECT_STATUSES,
  PROSPECT_TIERS,
  isProspectTier,
  tierResultMessage,
  useSetProspectTier,
  findLeadByEmail,
  isProspectStatus,
  prospectErrorMessage,
  prospectName,
  prospectStatusLabel,
  useLogProspectActivity,
  usePromoteProspect,
  useProspectActivity,
  useSetProspectStatus,
  useUpdateProspect,
} from './prospects';
import type { Prospect, ProspectChanges } from './prospects';
import { ProspectDmActions } from './ProspectDmActions';
import { ProspectTierChip } from './ProspectTierChip';
import { todayIso } from './trackerFormat';

/**
 * One prospect, whole: the six columns in full, the status control, the
 * promotion, and the history of chasing them.
 *
 * Fields save on blur, one at a time, and only when changed — the same shape
 * as the Deals Tasks rows. Status is not a field: it goes through the RPC so
 * the work it records lands with it.
 */
export function ProspectDrawer({
  prospect,
  onClose,
}: {
  prospect: Prospect | null;
  onClose: () => void;
}) {
  return (
    <Drawer open={prospect !== null} title="Prospect" onClose={onClose} wide>
      {prospect === null ? null : (
        <ProspectBody key={prospect.id} prospect={prospect} onClose={onClose} />
      )}
    </Drawer>
  );
}

const FIELDS: { key: keyof ProspectChanges; label: string; wide?: boolean; long?: boolean }[] = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'company_name', label: 'Company' },
  { key: 'title', label: 'Job title' },
  { key: 'linkedin_url', label: 'LinkedIn URL', wide: true },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'why_relevant', label: 'Why relevant', wide: true, long: true },
  { key: 'opening_hook', label: 'Opening hook', wide: true, long: true },
];

function ProspectBody({ prospect, onClose }: { prospect: Prospect; onClose: () => void }) {
  const { client } = useAuth();
  const isAdmin = useJourneyAdmin();
  const { message_enabled, operations_enabled } = useProspectingAvailability();
  const remove = useDeleteProspect();
  const update = useUpdateProspect();
  const setTier = useSetProspectTier();
  const [tierMessage, setTierMessage] = useState('');
  const setStatus = useSetProspectStatus();
  const logActivity = useLogProspectActivity();
  const queueOne = useQueueProspectResearch();
  const queue = useResearchQueue();
  const request = latestByProspect(queue.data ?? []).get(prospect.id);
  const promote = usePromoteProspect();
  const activity = useProspectActivity(prospect.id);
  const [messageActivity, setMessageActivity] = useState<string | null>(null);
  const selectedMessage = activity.data?.find((row) => row.id === messageActivity);
  const people = useTapPeople(true);
  const [draft, setDraft] = useState<ProspectChanges>({});
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [attach, setAttach] = useState<{ id: string; name: string } | null>(null);
  const [promotedTo, setPromotedTo] = useState<string | null>(prospect.promoted_lead_id);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [atomicDeleteStarted, setAtomicDeleteStarted] = useState(false);

  useEffect(() => {
    setPromotedTo(prospect.promoted_lead_id);
  }, [prospect.promoted_lead_id]);

  const promoted = prospect.promoted_at !== null || promotedTo !== null;
  const actorNames = new Map(
    (people.data ?? []).map((person) => [
      person.id,
      person.display_name === '' ? person.email : person.display_name,
    ]),
  );
  const items = mergeTimeline(
    leadActivityItems(activity.data ?? [], (row) => actorLabel(row, actorNames)).map((item) => {
      const entry = (activity.data ?? []).find((row) => `lead-${row.id}` === item.id);
      return entry === undefined ? item : { ...item, when: activityDayLabel(entry) };
    }),
  );

  function valueOf(key: keyof ProspectChanges): string {
    const pending = draft[key];
    return pending === undefined ? prospect[key] : pending;
  }

  function saveField(key: keyof ProspectChanges) {
    const pending = draft[key];
    if (pending === undefined || pending === prospect[key]) return;
    update.mutate(
      { id: prospect.id, changes: { [key]: pending } },
      { onError: (cause) => setError(prospectErrorMessage(cause.message)) },
    );
  }

  function changeStatus(next: string) {
    if (!isProspectStatus(next) || next === prospect.status) return;
    setError('');
    setStatus.mutate(
      { prospectId: prospect.id, status: next },
      { onError: (cause) => setError(prospectErrorMessage(cause.message)) },
    );
  }

  function addNote() {
    if (note.trim() === '') return;
    logActivity.mutate(
      { prospectId: prospect.id, kind: 'note', body: note.trim() },
      {
        onSuccess: () => setNote(''),
        onError: (cause) => setError(prospectErrorMessage(cause.message)),
      },
    );
  }

  function promoteNow(leadId?: string) {
    setError('');
    setAttach(null);
    promote.mutate(
      { prospectId: prospect.id, ...(leadId === undefined ? {} : { leadId }) },
      {
        onSuccess: (id) => setPromotedTo(id),
        onError: (cause) => {
          if (cause.message.includes('lead_email_exists')) {
            void findLeadByEmail(client, prospect.email).then((lead) => {
              if (lead === null) {
                setError(prospectErrorMessage(cause.message));
              } else {
                setAttach({
                  id: lead.id,
                  name: `${lead.first_name} ${lead.last_name}`.trim() || prospect.email,
                });
              }
            });
            return;
          }
          setError(prospectErrorMessage(cause.message));
        },
      },
    );
  }

  return (
    <div>
      {message_enabled && selectedMessage !== undefined && (
        <MessageSelectionDialog
          subject={{ kind: 'prospect', id: prospect.id }}
          activity={selectedMessage}
          detailActivity={selectedMessage}
          onClose={() => setMessageActivity(null)}
        />
      )}
      {error !== '' && <InlineError>{error}</InlineError>}

      <div className="prospect-drawer-actions">
        <ProspectTierChip tier={prospect.tier} />
        <Select
          label="Prospect tier"
          value={prospect.tier ?? ''}
          disabled={setTier.isPending}
          options={[
            { value: '', label: 'Not tagged' },
            ...PROSPECT_TIERS.map((entry) => ({ value: entry.key, label: entry.label })),
          ]}
          onChange={(e) => {
            const next = e.target.value;
            if (next !== '' && !isProspectTier(next)) return;
            setTierMessage('');
            setTier.mutate(
              { ids: [prospect.id], tier: isProspectTier(next) ? next : null },
              {
                onSuccess: (result) => setTierMessage(tierResultMessage(result)),
                onError: (cause) => setError(prospectErrorMessage(cause.message)),
              },
            );
          }}
        />
        {promoted ? (
          <>
            <span className="prospecting-promoted">
              Promoted to a lead
              {prospect.promoted_at === null ? '' : ` · ${todayLabel(prospect.promoted_at)}`}
            </span>
            {promotedTo === null ? null : (
              <Link className="profile-open" to={leadHref(promotedTo)}>
                Open lead
              </Link>
            )}
          </>
        ) : (
          <>
            <Select
              label="Status"
              labelHidden
              value={prospect.status}
              options={PROSPECT_STATUSES.map((entry) => ({ value: entry.key, label: entry.label }))}
              onChange={(e) => changeStatus(e.target.value)}
              disabled={setStatus.isPending}
            />
            <Button size="lg" onClick={() => promoteNow()} disabled={promote.isPending}>
              {promote.isPending ? 'Promoting…' : 'Promote to lead'}
            </Button>
            {prospect.linkedin_url === '' ? null : (
              <a
                className="profile-open"
                href={prospect.linkedin_url}
                target="_blank"
                rel="noreferrer"
              >
                Open LinkedIn
              </a>
            )}
          </>
        )}
      </div>

      {promoted ? null : <ProspectDmActions prospect={prospect} />}

      {tierMessage === '' ? null : <p role="status">{tierMessage}</p>}

      {promoted ? null : (
        <div className="prospect-drawer-section">
          <p className="prospect-drawer-section-title">Research and draft</p>
          {/* One button. Senku researches the company overnight, Reigen
              suggests the angle after him, and both land in the Activity
              timeline below under their own names. Nothing here is a touch. */}
          {canQueue(request) ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={queueOne.isPending}
              onClick={() =>
                queueOne.mutate(prospect.id, {
                  onError: (cause) => setError(prospectErrorMessage(cause.message)),
                })
              }
            >
              {request === undefined ? 'Research and draft' : 'Research again'}
            </Button>
          ) : (
            <p className="prospect-drawer-note">{requestStatusLabel(request)}</p>
          )}
          {request?.state === 'failed' ? (
            <p className="prospect-drawer-note">{requestStatusLabel(request)}</p>
          ) : null}
        </div>
      )}

      {promoted ? null : (
        <div className="prospect-drawer-section">
          <p className="prospect-drawer-section-title">Next action</p>
          <NextActionBlock
            recordId={prospect.id}
            recordKind="prospect"
            onError={(cause) =>
              setError(
                cause instanceof Error
                  ? cause.message
                  : 'That change could not be saved. Try again.',
              )
            }
          />
        </div>
      )}

      {attach === null ? null : (
        <div className="prospect-drawer-actions">
          <span>
            A lead with this email already exists: <strong>{attach.name}</strong>.
          </span>
          <Button size="sm" variant="secondary" onClick={() => promoteNow(attach.id)}>
            Attach to that lead
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAttach(null)}>
            Cancel
          </Button>
        </div>
      )}

      <div className="prospect-drawer-grid">
        {FIELDS.map((field) =>
          field.long === true ? (
            <div
              key={field.key}
              className={field.wide === true ? 'prospect-drawer-wide' : undefined}
            >
              <Textarea
                label={field.label}
                rows={3}
                value={valueOf(field.key)}
                disabled={promoted}
                onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
                onBlur={() => saveField(field.key)}
              />
            </div>
          ) : (
            <div
              key={field.key}
              className={field.wide === true ? 'prospect-drawer-wide' : undefined}
            >
              <Input
                label={field.label}
                value={valueOf(field.key)}
                disabled={promoted}
                onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
                onBlur={() => saveField(field.key)}
              />
            </div>
          ),
        )}
      </div>

      <ProspectHistorySection subject={{ table: 'prospects', id: prospect.id }} />
      <div className="prospect-drawer-section">
        <p className="prospect-drawer-section-title">Activity</p>
        {promoted ? (
          <p className="prospecting-promoted">
            This history moved to the lead when it was promoted. Log new work there.
          </p>
        ) : (
          <div className="prospect-drawer-actions">
            <Input
              label="Note"
              labelHidden
              placeholder="Add a note…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addNote();
              }}
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={addNote}
              disabled={logActivity.isPending}
            >
              Log note
            </Button>
          </div>
        )}
        {items.length === 0 ? (
          <p className="prospecting-promoted">
            Nothing logged yet. Use Log DM to record a message.
          </p>
        ) : (
          <Timeline
            items={items}
            initialLimit={20}
            actions={(item) => {
              const row = activity.data?.find((entry) => item.id === `lead-${entry.id}`);
              return message_enabled && row?.kind === 'linkedin' && row.source === 'manual' ? (
                <Button size="sm" variant="ghost" onClick={() => setMessageActivity(row.id)}>
                  Message used...
                </Button>
              ) : null;
            }}
          />
        )}
      </div>

      {promoted || (operations_enabled && !isAdmin) ? null : (
        <div className="prospect-drawer-section prospect-drawer-actions">
          <Button
            size="sm"
            variant={!operations_enabled && confirmDelete ? 'danger' : 'ghost'}
            disabled={remove.isPending || (atomicDeleteStarted && !operations_enabled)}
            onClick={() => {
              if (operations_enabled) setAtomicDeleteStarted(true);
              if (operations_enabled || !confirmDelete) {
                setConfirmDelete(true);
                return;
              }
              remove.mutate(prospect.id, {
                onSuccess: onClose,
                onError: (cause) => setError(prospectErrorMessage(cause.message)),
              });
            }}
          >
            {!operations_enabled && confirmDelete ? 'Really delete prospect' : 'Delete prospect'}
          </Button>
          {atomicDeleteStarted && !operations_enabled && (
            <span role="status">This unfinished removal is paused until availability returns.</span>
          )}
          {confirmDelete && !operations_enabled && (
            <span className="prospecting-promoted">
              Removes {prospectName(prospect)} and everything logged on them.
            </span>
          )}
          {operations_enabled && confirmDelete ? (
            <JourneyOperationDialog
              intents={[
                {
                  kind: 'cleanup',
                  table: 'prospects',
                  id: prospect.id,
                  label: prospectName(prospect),
                },
              ]}
              onClose={() => setConfirmDelete(false)}
              onSaved={onClose}
            />
          ) : null}
        </div>
      )}
      <p className="prospecting-promoted">
        {prospect.source === 'senku_daily' ? 'From Senku' : 'Added by hand'} ·{' '}
        {prospectStatusLabel(prospect.status)} ·{' '}
        {prospect.sourced_on === null
          ? `added ${todayLabel(prospect.created_at)}`
          : `found ${prospect.sourced_on}`}
      </p>
    </div>
  );
}

function todayLabel(iso: string): string {
  const day = iso.slice(0, 10);
  return day === todayIso() ? 'today' : day;
}
